import maplibregl from 'maplibre-gl';

export type ControlPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface ControlOptions {
  position?: ControlPosition;
}

/**
 * Base control. Subclasses either return a MapLibre IControl adapter via
 * `getMaplibreAdapter()` (preferred — gets attached via real `map.addControl()`)
 * or render their own DOM into the SDK overlay layer through `render()`.
 */
export abstract class Control {
  protected position: ControlPosition;
  protected element: HTMLElement;
  protected map: any;
  protected visible: boolean = true;
  protected _attachedMaplibreControl: maplibregl.IControl | null = null;

  constructor(options: ControlOptions = {}) {
    this.position = options.position || 'top-right';
    this.element = document.createElement('div');
    this.element.className = 'migis-control';
  }

  setPosition(p: ControlPosition): void { this.position = p; }
  getPosition(): ControlPosition { return this.position; }
  setVisible(v: boolean): void { this.visible = v; this.element.style.display = v ? '' : 'none'; }
  isVisible(): boolean { return this.visible; }
  getElement(): HTMLElement { return this.element; }
  setMap(map: any): void { this.map = map; }

  /** Override to provide a MapLibre IControl adapter — MIMap will call map.addControl(). */
  getMaplibreAdapter(): unknown { return null; }

  /** Called by MIMap when the control is being added. Default = no-op overlay. */
  render(): void {}

  destroy(): void {
    if (this._attachedMaplibreControl && this.map?._maplibreMap) {
      try { this.map._maplibreMap.removeControl(this._attachedMaplibreControl); } catch {}
      this._attachedMaplibreControl = null;
    }
    this.element.remove();
  }
}

// Helpers ─────────────────────────────────────────────────────────────────────
function nativePos(p: ControlPosition): 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' {
  return p;
}

// ZoomControl ─────────────────────────────────────────────────────────────────
export class ZoomControl extends Control {
  private showCompass: boolean;
  private showZoom: boolean;
  private nativeCtl: maplibregl.NavigationControl | null = null;

  constructor(options: ControlOptions & { showCompass?: boolean; showZoom?: boolean } = {}) {
    super(options);
    this.showCompass = false;
    this.showZoom = options.showZoom !== false;
  }

  getMaplibreAdapter(): unknown {
    if (!this.nativeCtl) {
      this.nativeCtl = new maplibregl.NavigationControl({
        showCompass: this.showCompass, showZoom: this.showZoom, visualizePitch: false,
      });
    }
    this._attachedMaplibreControl = this.nativeCtl;
    return this.nativeCtl;
  }
}

// NavigationControl: zoom + compass + pitch ───────────────────────────────────
export class NavigationControl extends Control {
  private showCompass: boolean;
  private showZoom: boolean;
  private visualizePitch: boolean;
  private nativeCtl: maplibregl.NavigationControl | null = null;

  constructor(options: ControlOptions & { showCompass?: boolean; showZoom?: boolean; visualizePitch?: boolean } = {}) {
    super(options);
    this.showCompass = options.showCompass !== false;
    this.showZoom = options.showZoom !== false;
    this.visualizePitch = options.visualizePitch ?? true;
  }

  getMaplibreAdapter(): unknown {
    if (!this.nativeCtl) {
      this.nativeCtl = new maplibregl.NavigationControl({
        showCompass: this.showCompass, showZoom: this.showZoom, visualizePitch: this.visualizePitch,
      });
    }
    this._attachedMaplibreControl = this.nativeCtl;
    return this.nativeCtl;
  }
}

// ScaleBarControl ─────────────────────────────────────────────────────────────
export class ScaleBarControl extends Control {
  private units: 'metric' | 'imperial' | 'nautical';
  private maxWidth: number;
  private nativeCtl: maplibregl.ScaleControl | null = null;

  constructor(options: ControlOptions & { units?: 'metric' | 'imperial' | 'nautical'; minWidth?: number; maxWidth?: number } = {}) {
    super(options);
    this.units = options.units || 'metric';
    this.maxWidth = options.maxWidth || 100;
  }

  getMaplibreAdapter(): unknown {
    if (!this.nativeCtl) {
      this.nativeCtl = new maplibregl.ScaleControl({ maxWidth: this.maxWidth, unit: this.units });
    }
    this._attachedMaplibreControl = this.nativeCtl;
    return this.nativeCtl;
  }
}

// AttributionControl ──────────────────────────────────────────────────────────
export class AttributionControl extends Control {
  private compact: boolean;
  private customAttributions: string[];
  private nativeCtl: maplibregl.AttributionControl | null = null;

  constructor(options: ControlOptions & { compact?: boolean; customAttribution?: string | string[] } = {}) {
    super(options);
    this.compact = options.compact ?? false;
    this.customAttributions = options.customAttribution
      ? (Array.isArray(options.customAttribution) ? options.customAttribution : [options.customAttribution])
      : [];
  }

  getMaplibreAdapter(): unknown {
    if (!this.nativeCtl) {
      this.nativeCtl = new maplibregl.AttributionControl({
        compact: this.compact,
        customAttribution: this.customAttributions.length ? this.customAttributions : undefined,
      });
    }
    this._attachedMaplibreControl = this.nativeCtl;
    return this.nativeCtl;
  }
}

// FullScreenControl ───────────────────────────────────────────────────────────
export class FullScreenControl extends Control {
  private nativeCtl: maplibregl.FullscreenControl | null = null;

  getMaplibreAdapter(): unknown {
    if (!this.nativeCtl) this.nativeCtl = new maplibregl.FullscreenControl();
    this._attachedMaplibreControl = this.nativeCtl;
    return this.nativeCtl;
  }
}

// GeolocationControl ─────────────────────────────────────────────────────────
export class GeolocationControl extends Control {
  private trackUserLocation: boolean;
  private nativeCtl: maplibregl.GeolocateControl | null = null;

  constructor(options: ControlOptions & { trackUserLocation?: boolean } = {}) {
    super(options);
    this.trackUserLocation = options.trackUserLocation ?? false;
  }

  getMaplibreAdapter(): unknown {
    if (!this.nativeCtl) {
      this.nativeCtl = new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: this.trackUserLocation,
      });
    }
    this._attachedMaplibreControl = this.nativeCtl;
    return this.nativeCtl;
  }
}

// CoordinateDisplayControl — custom DOM overlay ───────────────────────────────
export class CoordinateDisplayControl extends Control {
  private format: 'dd' | 'dms';
  private precision: number;
  private _onMove: ((e: any) => void) | null = null;

  constructor(options: ControlOptions & { format?: 'dd' | 'dms'; precision?: number } = {}) {
    super(options);
    this.format = options.format || 'dd';
    this.precision = options.precision ?? 6;
  }

  render(): void {
    this.element.className = 'migis-control migis-coordinate-display';
    Object.assign(this.element.style, {
      position: 'absolute', padding: '4px 8px', background: 'rgba(255,255,255,0.85)',
      borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace',
      zIndex: '5', pointerEvents: 'none',
      [this.position.includes('top') ? 'top' : 'bottom']: '8px',
      [this.position.includes('left') ? 'left' : 'right']: '8px',
    } as any);
    this.element.textContent = '\u2014';
    const ml = this.map?._maplibreMap;
    if (ml) {
      const container = ml.getContainer();
      container.appendChild(this.element);
      this._onMove = (e: any) => {
        const lng = e.lngLat.lng, lat = e.lngLat.lat;
        if (this.format === 'dms') {
          this.element.textContent = `${this._dms(lat, true)}, ${this._dms(lng, false)}`;
        } else {
          this.element.textContent = `${lat.toFixed(this.precision)}, ${lng.toFixed(this.precision)}`;
        }
      };
      ml.on('mousemove', this._onMove);
    }
  }

  private _dms(d: number, isLat: boolean): string {
    const sign = d < 0 ? (isLat ? 'S' : 'W') : (isLat ? 'N' : 'E');
    const abs = Math.abs(d);
    const deg = Math.floor(abs);
    const min = Math.floor((abs - deg) * 60);
    const sec = ((abs - deg) * 60 - min) * 60;
    return `${deg}\u00b0${min}'${sec.toFixed(2)}"${sign}`;
  }

  destroy(): void {
    if (this._onMove && this.map?._maplibreMap) {
      try { this.map._maplibreMap.off('mousemove', this._onMove); } catch {}
    }
    super.destroy();
  }
}

// LayerSwitcherControl — DOM overlay ──────────────────────────────────────────
export class LayerSwitcherControl extends Control {
  private layers: { id: string; label: string }[];

  constructor(options: ControlOptions & { layers?: { id: string; label: string }[] } = {}) {
    super(options);
    this.layers = options.layers || [];
  }

  render(): void {
    this.element.className = 'migis-control migis-layer-switcher';
    Object.assign(this.element.style, {
      position: 'absolute', padding: '8px', background: 'rgba(255,255,255,0.95)',
      borderRadius: '4px', fontSize: '12px', zIndex: '5',
      [this.position.includes('top') ? 'top' : 'bottom']: '8px',
      [this.position.includes('left') ? 'left' : 'right']: '8px',
    } as any);
    this.element.innerHTML = '';
    const sdkMap = this.map;
    for (const l of this.layers) {
      const row = document.createElement('label');
      row.style.display = 'block';
      const cb = document.createElement('input');
      cb.type = 'checkbox'; cb.checked = true;
      cb.onchange = () => {
        const layer = sdkMap?.getLayer?.(l.id);
        if (layer && typeof layer.setVisible === 'function') layer.setVisible(cb.checked);
      };
      row.appendChild(cb);
      row.appendChild(document.createTextNode(' ' + l.label));
      this.element.appendChild(row);
    }
    const ml = this.map?._maplibreMap;
    if (ml) ml.getContainer().appendChild(this.element);
  }
}

// MeasureControl — wraps a MeasureInteraction ─────────────────────────────────
export class MeasureControl extends Control {
  private units: 'metric' | 'imperial' | 'nautical';
  private mode: 'distance' | 'area';
  private _interaction: any = null;
  private _btn: HTMLButtonElement | null = null;

  constructor(options: ControlOptions & { units?: 'metric' | 'imperial' | 'nautical'; mode?: 'distance' | 'area' } = {}) {
    super(options);
    this.units = options.units || 'metric';
    this.mode = options.mode || 'distance';
  }

  render(): void {
    this.element.className = 'migis-control migis-measure-control';
    Object.assign(this.element.style, {
      position: 'absolute', padding: '4px', background: 'rgba(255,255,255,0.95)',
      borderRadius: '4px', zIndex: '5',
      [this.position.includes('top') ? 'top' : 'bottom']: '40px',
      [this.position.includes('left') ? 'left' : 'right']: '8px',
    } as any);
    const btn = document.createElement('button');
    btn.textContent = 'Measure';
    btn.onclick = () => this.toggle();
    this._btn = btn;
    this.element.appendChild(btn);
    const ml = this.map?._maplibreMap;
    if (ml) ml.getContainer().appendChild(this.element);
  }

  toggle(): void {
    if (!this.map) return;
    if (this._interaction) {
      this.map.removeInteraction(this._interaction);
      try { this._interaction.destroy?.(); } catch {}
      this._interaction = null;
      if (this._btn) this._btn.textContent = 'Measure';
    } else {
      // Lazy import to avoid circular dep
      const { MeasureInteraction } = require('../interactions/Interaction');
      this._interaction = new MeasureInteraction({ type: this.mode === 'area' ? 'area' : 'distance', units: this.units });
      this.map.addInteraction(this._interaction);
      if (this._btn) this._btn.textContent = 'Stop';
    }
  }

  destroy(): void {
    if (this._interaction && this.map) {
      this.map.removeInteraction(this._interaction);
      try { this._interaction.destroy?.(); } catch {}
    }
    super.destroy();
  }
}

// DrawControl — wraps DrawInteraction ─────────────────────────────────────────
export class DrawControl extends Control {
  private types: Array<'point' | 'line' | 'polygon'>;
  private _interaction: any = null;

  constructor(options: ControlOptions & { types?: Array<'point' | 'line' | 'polygon'> } = {}) {
    super(options);
    this.types = options.types || ['point', 'line', 'polygon'];
  }

  render(): void {
    this.element.className = 'migis-control migis-draw-control';
    Object.assign(this.element.style, {
      position: 'absolute', padding: '4px', background: 'rgba(255,255,255,0.95)',
      borderRadius: '4px', display: 'flex', gap: '4px', zIndex: '5',
      [this.position.includes('top') ? 'top' : 'bottom']: '8px',
      [this.position.includes('left') ? 'left' : 'right']: '120px',
    } as any);
    for (const t of this.types) {
      const btn = document.createElement('button');
      btn.textContent = t;
      btn.onclick = () => this.activate(t);
      this.element.appendChild(btn);
    }
    const ml = this.map?._maplibreMap;
    if (ml) ml.getContainer().appendChild(this.element);
  }

  activate(type: 'point' | 'line' | 'polygon'): void {
    if (!this.map) return;
    if (this._interaction) {
      this.map.removeInteraction(this._interaction);
      try { this._interaction.destroy?.(); } catch {}
    }
    const { DrawInteraction } = require('../interactions/Interaction');
    this._interaction = new DrawInteraction({ type });
    this.map.addInteraction(this._interaction);
  }

  destroy(): void {
    if (this._interaction && this.map) {
      this.map.removeInteraction(this._interaction);
      try { this._interaction.destroy?.(); } catch {}
    }
    super.destroy();
  }
}

// Compass, Print, Search, Bookmark, Legend, Graticule, TimeSlider,
// OverviewMap, ElevationProfile — minimal DOM overlays with concrete behavior
// where it's cheap to provide it. (Each renders a simple labeled chip.)

class _SimpleOverlayControl extends Control {
  protected label = 'Control';
  render(): void {
    this.element.className = 'migis-control';
    Object.assign(this.element.style, {
      position: 'absolute', padding: '4px 8px', background: 'rgba(255,255,255,0.9)',
      borderRadius: '4px', fontSize: '12px', zIndex: '5',
      [this.position.includes('top') ? 'top' : 'bottom']: '70px',
      [this.position.includes('left') ? 'left' : 'right']: '8px',
    } as any);
    this.element.textContent = this.label;
    const ml = this.map?._maplibreMap;
    if (ml) ml.getContainer().appendChild(this.element);
  }
}

export class CompassControl extends _SimpleOverlayControl {
  protected label = 'N';
  private _onRot: ((e: any) => void) | null = null;
  render(): void {
    super.render();
    const ml = this.map?._maplibreMap;
    if (ml) {
      this._onRot = () => {
        const b = ml.getBearing();
        this.element.style.transform = `rotate(${-b}deg)`;
      };
      ml.on('rotate', this._onRot); ml.on('move', this._onRot);
    }
  }
  destroy(): void {
    const ml = this.map?._maplibreMap;
    if (this._onRot && ml) { try { ml.off('rotate', this._onRot); ml.off('move', this._onRot); } catch {} }
    super.destroy();
  }
}

export class OverviewMapControl extends _SimpleOverlayControl { protected label = 'Overview'; }
export class PrintControl extends _SimpleOverlayControl { protected label = 'Print'; }
export class SearchControl extends _SimpleOverlayControl { protected label = 'Search'; }
export class LegendControl extends _SimpleOverlayControl { protected label = 'Legend'; }
export class GraticuleControl extends _SimpleOverlayControl { protected label = 'Graticule'; }
export class TimeSliderControl extends _SimpleOverlayControl { protected label = 'Time'; }
export class ElevationProfileControl extends _SimpleOverlayControl { protected label = 'Elevation'; }

export class BookmarkControl extends Control {
  private bookmarks: Array<{ name: string; center: [number, number]; zoom: number }>;
  constructor(options: ControlOptions & { bookmarks?: Array<{ name: string; center: [number, number]; zoom: number }> } = {}) {
    super(options);
    this.bookmarks = options.bookmarks || [];
  }
  render(): void {
    this.element.className = 'migis-control migis-bookmark-control';
    Object.assign(this.element.style, {
      position: 'absolute', padding: '4px', background: 'rgba(255,255,255,0.95)',
      borderRadius: '4px', display: 'flex', gap: '4px', flexDirection: 'column', zIndex: '5',
      [this.position.includes('top') ? 'top' : 'bottom']: '8px',
      [this.position.includes('left') ? 'left' : 'right']: '200px',
    } as any);
    for (const b of this.bookmarks) {
      const btn = document.createElement('button');
      btn.textContent = b.name;
      btn.onclick = () => {
        this.map?.flyTo?.({ center: b.center, zoom: b.zoom, duration: 1200 });
      };
      this.element.appendChild(btn);
    }
    const ml = this.map?._maplibreMap;
    if (ml) ml.getContainer().appendChild(this.element);
  }
}
